import { gql } from "@apollo/client";
import { ISkill } from "types/Skills.type";
import {
  ECertificationType,
  ETypeCertification,
  EUserSeniority,
  IUser,
  IUserCertification
} from "types/User.types";

export interface IStartOffboardingInput {
  reason: string; // requerido
  dateOfExpulsion: string; // requerido, formato ISO
  requireForwardingTo?: string[]; // opcional
  requireSharePointAccessTo?: string[]; // opcional
  userWhoHasBeenExpelled?: string; // opcional
  emailBackUp?: boolean; // opcional
  type?: string; // opcional (OperativeTaskTypeEnum)
  offboardingCausal?: string; // opcional (OffboardingCausalEnum)
}

export interface IStartOffboardingRes {
  startOffboarding: {
    id: string;
    reason: string;
    dateOfExpulsion: string;
  };
}

export interface IUpdateOperativeTaskInput {
  id?: string;
  dateOfExpulsion?: Date | string;
  requireForwardingTo?: string[];
  requireSharePointAccessTo?: string[];
  message?: string;
}

export interface ISendCollaboratorWelcomeEmailInput {
  collaboratorId: string;
  collaboratorEmail: string;
  collaboratorName: string;
  role?: string;
  department?: string;
  startDate?: string;
  password?: string;
  additionalMessage?: string;
}

const START_OFFBOARDING = gql`
  mutation startOffboarding($startOffboardingInput: ExpellFromGuildInputType!) {
    startOffboarding(startOffboardingInput: $startOffboardingInput) {
      id
      reason
      dateOfExpulsion
      offboardingCausal
      userWhoHasBeenExpelled
    }
  }
`;

const SIGNUP = gql`
  mutation Signup($signUpInput: CreateUserInput!) {
    signup(signUpInput: $signUpInput) {
      accessToken
      refreshToken
    }
  }
`;

export interface IUpdateUserSeniorityInput {
  user: string;
  seniority: EUserSeniority;
}

const UPDATE_USER_SENIORITY = gql`
  mutation updateUserSeniority(
    $updateUserSeniorityInput: UpdateUserSeniorityInput!
  ) {
    updateUserSeniority(updateUserSeniorityInput: $updateUserSeniorityInput) {
      id
      seniority
    }
  }
`;

export interface IUpdateUserNotificationTokenInput {
  token: string;
}

const UPDATE_USER_NOTIFICATION_TOKEN = gql`
  mutation updateUserNotificationToken(
    $updateUserNotificationTokenInput: UpdateUserNotificationTokenInput!
  ) {
    updateUserNotificationToken(
      updateUserNotificationTokenInput: $updateUserNotificationTokenInput
    )
  }
`;

export interface IUpdateUserScreenTime {
  screenTime: number;
}

const UPDATE_USER_SCREEN_TIME = gql`
  mutation updateUserScreenTime(
    $updateUserScreenTimeInput: UpdateUserScreenTimeInput!
  ) {
    updateUserScreenTime(
      updateUserScreenTimeInput: $updateUserScreenTimeInput
    ) {
      id
    }
  }
`;

export interface IChangePasswordRequestInput {
  email: string;
}

const CHANGE_PASSWORD = gql`
  mutation ChangePassword(
    $changePasswordRequestInput: ChangePasswordRequestInput!
  ) {
    changePassword(changePasswordRequestInput: $changePasswordRequestInput) {
      id
      name
      email
    }
  }
`;

export interface IChangePasswordInput {
  passwordRecoveryToken: string;
  userId: string;
  password: string;
}

const DO_RESET_PASSWORD = gql`
  mutation DoResetPassword($changePasswordInput: ChangePasswordInput!) {
    doResetPassword(changePasswordInput: $changePasswordInput) {
      id
    }
  }
`;

const UPDATE_USER_PROFILE = gql`
  mutation updateProfile($updateProfileInput: UpdateProfileInput!) {
    updateProfile(updateProfileInput: $updateProfileInput) {
      id
      name
      lastname
      linkedin
      instagram
      coverImage
      profileImage
      username
      email
      shortBio
      lang
      birthday
      skills {
        id
        name
      }
      aiCVInfo {
        education {
          dateEnd
          dateStart
          description
          title
        }
        languages {
          level
          name
        }
        tagLine
        workExperience {
          date
          description
          title
          company
        }
        extraWorkExperience {
          date
          description
          title
          company
        }
      }
    }
  }
`;

const UPDATE_USER_PROFILE_AVERAGE_SKILL = gql`
  mutation UpdateProfileWithAverageSkill(
    $updateProfileInput: UpdateProfileInput!
  ) {
    updateProfileWithAverageSkill(updateProfileInput: $updateProfileInput) {
      id
      name
      lastname
      linkedin
      instagram
      email
      shortBio
      lang
      skills {
        id
        name
      }
    }
  }
`;

export interface IUpdateProfileSkillsInput {
  skills: ISkill[];
}

const UPDATE_PROFILE_SKILLS = gql`
  mutation updateProfileSkills(
    $updateProfileSkillsInput: UpdateProfileSkillsInput!
  ) {
    updateProfileSkills(updateProfileSkillsInput: $updateProfileSkillsInput) {
      skills {
        id
      }
    }
  }
`;

export interface IUserNotifyInputInput {
  name: string;
  email: string;
}
export interface IUserNotifyInput {
  text: string;
  to?: string;
  from: string;
  type: string;
}

const USER_NOTIFY = gql`
  mutation userNotify($userNotifyInput: UserNotifyInput!) {
    userNotify(userNotifyInput: $userNotifyInput)
  }
`;

export interface IUpdateUserSelectedVendorInput {}

const UPDATE_USER_SELECTED_VENDOR = gql`
  mutation updateUserSelectedVendor(
    $updateUserSelectedVendorInput: UpdateUserSelectedVendorInput!
  ) {
    updateUserSelectedVendor(
      updateUserSelectedVendorInput: $updateUserSelectedVendorInput
    ) {
      id
    }
  }
`;

const REFRESH_ACCESS_TOKEN = gql`
  mutation refreshAccessToken(
    $refreshAccessTokenInput: RefreshAccessTokenInput!
  ) {
    refreshAccessToken(refreshAccessTokenInput: $refreshAccessTokenInput) {
      accessToken
    }
  }
`;

export interface IAddRoleVendorToUserInput {
  systemRole: string;
  role: string;
  email: string[];
}

const ASSING_ROLE_AND_VENDOR_TO_USER = gql`
  mutation assingRoleAndVendorToUser(
    $addRoleAndVendorToUserInput: AddRoleAndVendorToUserInput!
  ) {
    assingRoleAndVendorToUser(
      addRoleAndVendorToUserInput: $addRoleAndVendorToUserInput
    )
  }
`;

const UPDATE_VENDOR_USER = gql`
  mutation updateVendorUser($updateVendorUserInput: UpdateVendorUserInput!) {
    updateVendorUser(updateVendorUserInput: $updateVendorUserInput) {
      id
    }
  }
`;

const TOGGLE_USER_LEADER = gql`
  mutation toggleUserLeader($userId: String!) {
    toggleUserLeader(userId: $userId) {
      id
    }
  }
`;

export interface IToggleUserMarketplaceAvailabilityInput {
  userId: string;
  available: boolean;
  availableFromDate?: string;
}

const TOGGLE_USER_MARKETPLACE_AVAILABILITY = gql`
  mutation toggleUserMarketplaceAvailability(
    $toggleUserMarketplaceAvailabilityInput: ToggleUserMarketplaceAvailabilityInput!
  ) {
    toggleUserMarketplaceAvailability(
      toggleUserMarketplaceAvailabilityInput: $toggleUserMarketplaceAvailabilityInput
    ) {
      id
      availableOnJobyfineMarketplace
      availableFromDate
    }
  }
`;

export interface ISocialNetworkLogInInput {
  accessToken: string;
}

export interface ISocialNetworkLogInRes {
  accessToken: string;
  refreshToken: string;
}

export const LOGIN_WITH_LINKEDIN = gql`
  mutation loginWithLinkedIn($linkedinLogInInput: SocialNetworkLogInInput!) {
    loginWithLinkedIn(linkedinLogInInput: $linkedinLogInInput) {
      accessToken
    }
  }
`;

export interface IRemoveUserFromVendorInput {
  user: string;
}

export interface IRemoveUserFromVendorRes {
  removeUserFromVendor: IUser;
}

export const REMOVE_USER_FROM_VENDOR = gql`
  mutation removeUserFromVendor(
    $removeUserFromVendorInput: RemoveUserFromVendorInput!
  ) {
    removeUserFromVendor(
      removeUserFromVendorInput: $removeUserFromVendorInput
    ) {
      id
    }
  }
`;

export interface IAddUserCertificationInput {
  name: string;
  expiracyDate?: Date;
  base64Certification?: string;
  certificationUrl?: string;
  type?: ECertificationType;
  certificationType?: ETypeCertification;
  emitDate?: Date;
}

export interface IAddUserCertificationRes {
  addUserCertification: IUserCertification;
}

export const ADD_USER_CERTIFICATION = gql`
  mutation AddUserCertification(
    $addUserCertificationInput: AddUserCertificationInput!
  ) {
    addUserCertification(
      addUserCertificationInput: $addUserCertificationInput
    ) {
      id
    }
  }
`;

export interface IDeleteUserCertificationInput {
  id: string;
}

export interface IDeleteUserCertificationRes {
  deleteUserCertification: IUserCertification;
}

export const DELETE_USER_CERTIFICATION = gql`
  mutation DeleteUserCertification(
    $deleteUserCertificationInput: DeleteUserCertificationInput!
  ) {
    deleteUserCertification(
      deleteUserCertificationInput: $deleteUserCertificationInput
    ) {
      id
    }
  }
`;

export interface IUpdateUserCertificationInput
  extends IAddUserCertificationInput {
  id: string;
}

export interface IUpdateUserCertificationRes {
  updateUserCertification: IUserCertification;
}

export const UPDATE_USER_CERTIFICATION = gql`
  mutation UpdateUserCertification(
    $updateUserCertificationInput: UpdateUserCertificationInput!
  ) {
    updateUserCertification(
      updateUserCertificationInput: $updateUserCertificationInput
    ) {
      id
    }
  }
`;

export interface IMicrosoftLoginResponse {
  accessToken: string;
  refreshToken: string;
}

const LOGIN_WITH_MICROSOFT = gql`
  mutation loginWithMicrosoft($microsoftLogInInput: SocialNetworkLogInInput!) {
    loginWithMicrosoft(microsoftLogInInput: $microsoftLogInInput) {
      accessToken
      refreshToken
    }
  }
`;

const UPDATE_EXTRA_INFO = gql`
  mutation UpdateExtraDataSilently($silentUpdateInput: SilentUpdateInput!) {
    updateExtraDataSilently(silentUpdateInput: $silentUpdateInput)
  }
`;

const CREATE_CV_WITH_AI = gql`
  mutation CreateCVWithAI($createCvWithAiInput: CreateCVWithAIInput!) {
    createCVWithAI(createCVWithAIInput: $createCvWithAiInput) {
      cvUrl
    }
  }
`;

const DEACTIVE_INVITATION = gql`
  mutation DeactivateUserInvitation($invitationId: String!) {
    deactivateUserInvitation(invitationId: $invitationId) {
      id
    }
  }
`;

const RESEND_USER_INVIATION = gql`
  mutation ResendUserInvitation($invitationId: String!) {
    resendUserInvitation(invitationId: $invitationId) {
      id
    }
  }
`;
const UPLOAD_USERS_BY_FILE = gql`
  mutation UploadUsersByFile($uploadUserFile: String!) {
    uploadUsersByFile(uploadUserFile: $uploadUserFile)
  }
`;

const UPDATE_USER_PROFILE_INDUSTRIES = gql`
  mutation UpdateUserIndustries($industries: [String!]!) {
    updateUserIndustries(industries: $industries) {
      id
    }
  }
`;

const UPDATE_USER_INDUSTRY_SCORE = gql`
  mutation UpdateUserIndustryScore(
    $updateIndustryScoreInput: UpdateIndustryScoreInput!
  ) {
    updateUserIndustryScore(
      updateIndustryScoreInput: $updateIndustryScoreInput
    ) {
      id
    }
  }
`;

const UPDATE_ASSIGNMENT_PROJECT = gql`
  mutation UpdateAssignmentProject(
    $updateAssignmentProjectInput: UpdateAssignmentProjectInput!
  ) {
    updateAssignmentProject(
      updateAssignmentProjectInput: $updateAssignmentProjectInput
    ) {
      id
    }
  }
`;

const HR_ACCEPT_OFFBOARDING = gql`
  mutation HrAcceptOffboarding(
    $hrAcceptOffboardingInput: HrAcceptOffboardingInput!
  ) {
    hrAcceptOffboarding(hrAcceptOffboardingInput: $hrAcceptOffboardingInput) {
      id
    }
  }
`;

const CLOUD_ACCEPT_OFFBOARDING = gql`
  mutation CloudAcceptOffboarding(
    $cloudAcceptOffboardingInput: CloudAcceptOffboardingInput!
  ) {
    cloudAcceptOffboarding(
      cloudAcceptOffboardingInput: $cloudAcceptOffboardingInput
    ) {
      id
    }
  }
`;

const OPERATIVE_TASK_ACCEPT_OFFBOARDING = gql`
  mutation OperativeTaskAcceptOffboarding(
    $operationAcceptOffboardingInput: OperationAcceptOffboardingInput!
  ) {
    operationAcceptOffboarding(
      operationAcceptOffboardingInput: $operationAcceptOffboardingInput
    ) {
      id
    }
  }
`;

const FINANCE_ACCEPT_OFFBOARDING = gql`
  mutation FinanceAcceptOffboarding(
    $financeAcceptOffboardingInput: FinanceAcceptOffboardingInput!
  ) {
    financeAcceptOffboarding(
      financeAcceptOffboardingInput: $financeAcceptOffboardingInput
    ) {
      id
    }
  }
`;

const HR_ACCEPT_ONBOARDING = gql`
  mutation HrAcceptOnboarding(
    $hrAcceptOnboardingInput: HrAcceptOffboardingInput!
  ) {
    hrAcceptOnboarding(hrAcceptOnboardingInput: $hrAcceptOnboardingInput) {
      id
    }
  }
`;

const CLOUD_ACCEPT_ONBOARDING = gql`
  mutation CloudAcceptOnboarding(
    $cloudAcceptOnboardingInput: CloudAcceptOffboardingInput!
  ) {
    cloudAcceptOnboarding(
      cloudAcceptOnboardingInput: $cloudAcceptOnboardingInput
    ) {
      id
    }
  }
`;

const OPERATION_ACCEPT_ONBOARDING = gql`
  mutation OperationAcceptOnboarding(
    $operationAcceptOnboardingInput: OperationAcceptOffboardingInput!
  ) {
    operationAcceptOnboarding(
      operationAcceptOnboardingInput: $operationAcceptOnboardingInput
    ) {
      id
    }
  }
`;

const UPDATE_OPERATIVE_TASK = gql`
  mutation UpdateOperativeTask(
    $updateOperativeTaskInput: UpdateOperativeTaskInput!
  ) {
    updateOperativeTask(updateOperativeTaskInput: $updateOperativeTaskInput) {
      id
      dateOfExpulsion
      requireForwardingTo
      requireSharePointAccessTo
    }
  }
`;

const SEND_COLLABORATOR_WELCOME_EMAIL = gql`
  mutation SendCollaboratorWelcomeEmail(
    $sendCollaboratorWelcomeEmailInput: SendCollaboratorWelcomeEmailInput!
  ) {
    sendCollaboratorWelcomeEmail(
      sendCollaboratorWelcomeEmailInput: $sendCollaboratorWelcomeEmailInput
    )
  }
`;

export interface ILinkApplicantToUserInput {
  userId: string;
  applicantId: string;
}

const LINK_APPLICANT_TO_USER = gql`
  mutation LinkApplicantToUser(
    $linkApplicantToUserInput: LinkApplicantToUserInput!
  ) {
    linkApplicantToUser(linkApplicantToUserInput: $linkApplicantToUserInput) {
      id
    }
  }
`;

const CANCEL_OFFBOARDING = gql`
  mutation CancelOffboarding(
    $cancelOffboardingInput: CancelOffboardingInputType!
  ) {
    cancelOffboarding(cancelOffboardingInput: $cancelOffboardingInput) {
      id
      cancelled
      cancelledReason
      cancelledAt
    }
  }
`;

const SEND_REMINDER_EMAIL = gql`
  mutation SendReminderEmail($id: String!, $team: OperativeTaskTeamEnum!) {
    sendReminderEmail(id: $id, team: $team)
  }
`;

export interface ICreateVendorUserDirectlyInput {
  name: string;
  lastname: string;
  email: string;
  systemRole: string;
  vendorId: string;
}

const CREATE_VENDOR_USER_DIRECTLY = gql`
  mutation CreateVendorUserDirectly(
    $createVendorUserDirectlyInput: CreateVendorUserDirectlyInput!
  ) {
    createVendorUserDirectly(
      createVendorUserDirectlyInput: $createVendorUserDirectlyInput
    ) {
      id
      name
      lastname
      email
      systemRole
    }
  }
`;

export default {
  LINK_APPLICANT_TO_USER,
  SIGNUP,
  CHANGE_PASSWORD,
  DO_RESET_PASSWORD,
  UPDATE_USER_PROFILE,
  UPDATE_USER_SELECTED_VENDOR,
  REFRESH_ACCESS_TOKEN,
  ASSING_ROLE_AND_VENDOR_TO_USER,
  TOGGLE_USER_LEADER,
  TOGGLE_USER_MARKETPLACE_AVAILABILITY,
  UPDATE_VENDOR_USER,
  LOGIN_WITH_LINKEDIN,
  UPDATE_USER_SCREEN_TIME,
  REMOVE_USER_FROM_VENDOR,
  ADD_USER_CERTIFICATION,
  UPDATE_USER_CERTIFICATION,
  DELETE_USER_CERTIFICATION,
  UPDATE_USER_NOTIFICATION_TOKEN,
  UPDATE_USER_PROFILE_AVERAGE_SKILL,
  UPDATE_PROFILE_SKILLS,
  UPDATE_USER_SENIORITY,
  USER_NOTIFY,
  LOGIN_WITH_MICROSOFT,
  UPDATE_EXTRA_INFO,
  CREATE_CV_WITH_AI,
  DEACTIVE_INVITATION,
  RESEND_USER_INVIATION,
  UPLOAD_USERS_BY_FILE,
  UPDATE_USER_PROFILE_INDUSTRIES,
  UPDATE_USER_INDUSTRY_SCORE,
  UPDATE_ASSIGNMENT_PROJECT,
  SEND_COLLABORATOR_WELCOME_EMAIL,
  START_OFFBOARDING,
  HR_ACCEPT_OFFBOARDING,
  CLOUD_ACCEPT_OFFBOARDING,
  FINANCE_ACCEPT_OFFBOARDING,
  OPERATIVE_TASK_ACCEPT_OFFBOARDING,
  HR_ACCEPT_ONBOARDING,
  CLOUD_ACCEPT_ONBOARDING,
  OPERATION_ACCEPT_ONBOARDING,
  UPDATE_OPERATIVE_TASK,
  CANCEL_OFFBOARDING,
  SEND_REMINDER_EMAIL,
  CREATE_VENDOR_USER_DIRECTLY
};
